import { useEditor } from "@smartfire/js-block-engine";
import React, { useCallback, useRef } from "react";
import mergeRefs from "react-merge-refs";
import { SmartForm } from "./SmartForm";
import { SmartFormContext } from "./SmartFormContext";


export type FormResult = {
  success: boolean;
  message?: string;
  code?: number;
  errors?: string;
};

export type FormRenderProps = {
  formApi: SmartForm;
};

export type FormProps = {
  children?: React.ReactNode;
  className?: React.AllHTMLAttributes<"div" | "form">["className"];
  /**
   * Override the form submit behavior.
   */
  onSubmit?: React.AllHTMLAttributes<"form">["onSubmit"];

  /**
   * Callback called when the form is submitted.
   */
  onSmartFormSubmit?: (submit: {
    formApi: SmartForm;
    formData: FormData;
    send: (formData: FormData) => Promise<FormResult>;
  }) => Promise<FormResult | void>;

  style?: React.AllHTMLAttributes<"div" | "form">["style"];
};

export const FormRender = React.forwardRef<
  HTMLDivElement | HTMLFormElement,
  FormProps & FormRenderProps
>(
  (
    {
      children, className, onSubmit, onSmartFormSubmit, style, formApi,
    }: FormProps & FormRenderProps,
    ref
  ) => {
    const { isEditing } = useEditor();
    const form = useRef<HTMLFormElement>(null);
    const formRef = mergeRefs([ref, form]);
    const formOnSubmit = useCallback(async (e) => {
      if (onSubmit) {
        return onSubmit(e);
      }

      e.preventDefault();
      e.stopPropagation();

      const callback: typeof onSmartFormSubmit = onSmartFormSubmit ?? (({send, formData}) => {
        return send(formData);
      });

      const formData = new FormData(e.target);

      const r = await callback({
        formApi,
        formData,
        send: async (formData) => {
          const result = await formApi.submit(formData);
          return result;
        },
      });

      if(r && r.success) {
        e.target.reset();
      }
    }, [formApi, onSubmit, onSmartFormSubmit, form]);
    return (
      <SmartFormContext.Provider value={formApi}>
        {isEditing ? (
          <div
            style={style}
            className={className}
            ref={ref as React.ForwardedRef<HTMLDivElement>}
          >
            {children}
          </div>
        ) : (
          <form
            style={style}
            className={className}
            ref={formRef}
            onSubmit={formOnSubmit}
          >
            {children}
            <input
              type="hidden"
              name="__smartform.formConfig"
              value={formApi.formConfig} />
          </form>
        )}
      </SmartFormContext.Provider>
    );
  }
);
